// Formatting only. This file never computes a number that gets stored.
//
// Rule 2: money arrives as a *string* because JSON numbers are IEEE doubles.
// Parsing one into a Number to format it is fine — parsing one to add it to
// another is not, and is why there is no add() here. If a screen needs a sum,
// the server sends it.

const GROUPER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * "1248.00" -> "1,24,800.00" style Indian grouping, two decimals always.
 * A shop owner reads ₹1,24,800 instantly and ₹124,800.00 twice.
 */
export function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return GROUPER.format(n)
}

export function formatRupees(value) {
  const formatted = formatMoney(value)
  return formatted === '—' ? formatted : `₹${formatted}`
}

/**
 * "₹1.2 Cr", "₹4.5 L", "₹12K" — for a chart axis or a tile where the exact
 * paisa would be noise. The way an Indian shop says a big number out loud.
 * Display only; the exact figure is always one hover or one table away.
 */
export function formatRupeesCompact(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  const sign = n < 0 ? '−' : ''
  const a = Math.abs(n)
  const trim = (x) => x.toFixed(x >= 100 ? 0 : 1).replace(/\.0$/, '')
  if (a >= 1e7) return `${sign}₹${trim(a / 1e7)} Cr`
  if (a >= 1e5) return `${sign}₹${trim(a / 1e5)} L`
  if (a >= 1e3) return `${sign}₹${trim(a / 1e3)}K`
  return `${sign}₹${Math.round(a)}`
}

/**
 * Quantities are never rounded — three decimals, trailing zeros trimmed.
 * Twelve boxes of a 1.62 sqft tile is 19.44 sqft, and a screen that shows 19.4
 * disagrees with the stock the server is holding.
 */
export function formatQty(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })
}

/** Whether a money string is greater than zero, without arithmetic on it. */
export function isPositive(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0
}

// ── the total in words ──────────────────────────────────────────────────────
//
// "Rupees One Lakh Twenty Four Thousand Eight Hundred Only" — the line every
// Indian tax invoice carries under the figure, in lakh/crore grouping rather
// than the western million/billion one.
//
// This lives in lib/ rather than in the billing feature because it is not the
// billing screen's: the posted-sale screen prints it, and so does the server
// when it renders the invoice document (backend/documents/words.py is the
// twin, and a test runs both over the same values). A shop that reads one
// wording on screen and another on paper has no way to tell which is the
// invoice — and where the words and the figures disagree, the words are what a
// court reads.
//
// Nothing below turns the amount into a Number before rounding it.
// `Number("99999999.995")` has already lost the paisa, and this is the one
// place on the invoice where being approximately right is indistinguishable
// from being wrong.

const ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
]

const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

/** 0–99. */
function underHundred(value) {
  if (value < 20) return ONES[value]
  const tens = TENS[Math.floor(value / 10)]
  const ones = ONES[value % 10]
  return ones ? `${tens} ${ones}` : tens
}

/** 0–999. */
function underThousand(value) {
  const hundreds = Math.floor(value / 100)
  const rest = value % 100
  const parts = []
  if (hundreds) parts.push(`${ONES[hundreds]} Hundred`)
  if (rest) parts.push(underHundred(rest))
  return parts.join(' ')
}

/** A whole number, in Indian grouping: crore, lakh, thousand, hundred. */
function wholeInWords(value) {
  if (value === 0) return 'Zero'

  const parts = []
  const crore = Math.floor(value / 10000000)
  let rest = value % 10000000
  const lakh = Math.floor(rest / 100000)
  rest %= 100000
  const thousand = Math.floor(rest / 1000)
  rest %= 1000

  if (crore) parts.push(`${wholeInWords(crore)} Crore`)
  if (lakh) parts.push(`${underThousand(lakh)} Lakh`)
  if (thousand) parts.push(`${underThousand(thousand)} Thousand`)
  if (rest) parts.push(underThousand(rest))

  return parts.join(' ')
}

/**
 * A money string -> the words printed under the total.
 *
 * The total on a posted bill is always whole rupees — §08 rounds it and carries
 * the difference in `round_off_amount` — but the paise branch is here because
 * this also renders a balance and a receipt, and those are not rounded.
 */
export function amountInWords(value) {
  if (value === null || value === undefined) return ''

  const text = String(value).trim()
  if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(text)) return ''

  const negative = text.startsWith('-')
  const body = text.replace(/^[+-]/, '')
  const [whole = '', fraction = ''] = body.split('.')

  // Round to paise on BigInt, halves away from zero — Python's ROUND_HALF_UP,
  // which is what an Indian tax invoice expects and what money.py does. The
  // third decimal is all that decides it, and everything beyond it can only
  // move a value that was already going up.
  const padded = (fraction + '000').slice(0, 3)
  let paise = BigInt((whole || '0') + padded.slice(0, 2))
  if (Number(padded[2]) >= 5) paise += 1n

  const rupees = paise / 100n
  const remainder = Number(paise % 100n)

  // Above nine quadrillion the digits are the artefact and the words stop
  // helping. Rule 9 caps this product at five crore of annual turnover, so no
  // single bill can legitimately come near it.
  if (rupees > BigInt(Number.MAX_SAFE_INTEGER)) return ''

  const parts = ['Rupees', wholeInWords(Number(rupees))]
  if (remainder) parts.push(`and ${underHundred(remainder)} Paise`)
  parts.push('Only')

  return `${negative ? 'Minus ' : ''}${parts.join(' ')}`
}

const DATE_FMT = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

/** "2026-08-12" -> "12 Aug 2026". Dates arrive already in IST from the server. */
export function formatDate(value) {
  if (!value) return '—'
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return String(value)
  // Constructed as UTC and formatted in UTC: the string is already the shop's
  // own date, and letting the browser's timezone touch it would shift a
  // 1 April invoice into 31 March for anyone west of IST.
  return DATE_FMT.format(new Date(Date.UTC(y, m - 1, d)))
}
